"use client";

import Link from "next/link";
import { useState } from "react";
import { useRouter } from "next/navigation";

import type {
  AuthErrorResponse,
  AuthValidationMessageKey,
  LoginSuccessResponse,
} from "@/lib/auth/types";
import type { Dictionary, Locale } from "@/lib/i18n";

import { ZayaMark } from "../layout/zaya-mark";

type LoginScreenProps = {
  dictionary: Dictionary;
  locale: Locale;
};

type LoginUiError = {
  message: string;
  messageKey?: AuthValidationMessageKey;
};

export function LoginScreen({ dictionary, locale }: LoginScreenProps) {
  const router = useRouter();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<LoginUiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const messageMap: Record<AuthValidationMessageKey, string> = {
    forbidden: dictionary.auth.errors.forbidden,
    identifierRequired: dictionary.auth.errors.identifierRequired,
    inactiveUser: dictionary.auth.errors.inactiveUser,
    invalidCredentials: dictionary.auth.errors.invalidCredentials,
    passwordRequired: dictionary.auth.errors.passwordRequired,
    unauthenticated: dictionary.auth.errors.unauthenticated,
    tooManyAttempts: dictionary.auth.errors.tooManyAttempts,
  };

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/auth/login", {
        body: JSON.stringify({
          identifier,
          locale,
          password,
        }),
        headers: {
          "content-type": "application/json",
        },
        method: "POST",
      });
      const payload = (await response.json()) as LoginSuccessResponse | AuthErrorResponse;

      if (!response.ok) {
        const messageKey =
          typeof payload === "object" &&
          payload !== null &&
          "messageKey" in payload &&
          typeof payload.messageKey === "string"
            ? (payload.messageKey as AuthValidationMessageKey)
            : undefined;

        setError({
          message:
            messageKey && messageMap[messageKey]
              ? messageMap[messageKey]
              : typeof payload === "object" &&
                  payload !== null &&
                  "error" in payload &&
                  typeof payload.error === "string"
                ? payload.error
                : dictionary.auth.errors.invalidCredentials,
          messageKey,
        });
        return;
      }

      setError(null);
      const successPayload = payload as LoginSuccessResponse;

      router.replace(successPayload.data.redirectPath);
      router.refresh();
    } catch {
      setError({
        message: dictionary.auth.errors.requestFailed,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="auth-layout">
      <section className="auth-card">
        <div className="auth-header">
          <ZayaMark size="md" />
          <div className="auth-brand-lockup">
            <strong className="auth-brand-title">{dictionary.common.appName}</strong>
            <span className="auth-brand-caption">
              {dictionary.common.platformLabel}
            </span>
          </div>
        </div>

        <div className="auth-copy">
          <p className="page-eyebrow">{dictionary.auth.loginEyebrow}</p>
          <h1>{dictionary.auth.loginTitle}</h1>
          <p className="page-description">{dictionary.auth.loginDescription}</p>
        </div>

        <form className="form-card" onSubmit={handleSubmit}>
          <label className="form-field">
            <span>{dictionary.auth.labels.identifier}</span>
            <input
              autoComplete="username"
              className="form-input"
              name="identifier"
              onChange={(event) => setIdentifier(event.target.value)}
              value={identifier}
            />
          </label>

          <label className="form-field">
            <span>{dictionary.auth.labels.password}</span>
            <div className="relative">
              <input
                autoComplete="current-password"
                className="form-input pe-11"
                name="password"
                onChange={(event) => setPassword(event.target.value)}
                type={showPassword ? "text" : "password"}
                value={password}
              />
              <button
                aria-label={
                  showPassword
                    ? locale === "ar"
                      ? "إخفاء كلمة المرور"
                      : "Hide password"
                    : locale === "ar"
                      ? "إظهار كلمة المرور"
                      : "Show password"
                }
                aria-pressed={showPassword}
                className="absolute end-2 top-1/2 inline-flex size-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
                onClick={() => setShowPassword((current) => !current)}
                tabIndex={0}
                type="button"
              >
                {showPassword ? (
                  // Eye-off (hide) — slashed eye
                  <svg
                    aria-hidden
                    className="size-4"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9.88 9.88a3 3 0 0 0 4.24 4.24" />
                    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                    <path d="m2 2 20 20" />
                  </svg>
                ) : (
                  // Eye (show)
                  <svg
                    aria-hidden
                    className="size-4"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </label>

          {error ? <p className="form-error-banner">{error.message}</p> : null}

          <div className="form-actions">
            <button className="button-link auth-submit-button" disabled={isSubmitting} type="submit">
              {isSubmitting ? dictionary.auth.actions.signingIn : dictionary.auth.actions.signIn}
            </button>
            <Link className="text-link" href={`/${locale}/forgot-password`}>
              {dictionary.forgotPassword.title}
            </Link>
          </div>
        </form>
      </section>
    </div>
  );
}
